import React, { FC, ReactNode } from 'react';
import cx from 'classnames';
import { APP_ACCENT, APP_TEXT } from '@/constant/theme/colors.constant';
import { Text } from './AppText';
import { tid } from '@/utils/testId';

// Usage
// -----
// Basic:       <AppRadio checked={val} onChange={setVal} />
// Label:       <AppRadio checked={val} onChange={setVal} label="Option A" />
// Label left:  <AppRadio checked={val} onChange={setVal} label="Option A" labelPosition="left" />
// Sizes:       xs | sm | md (default) | lg
// Colors:      primary (default) | blue | purple | green | red | orange | teal
// Variants:    filled (default) | outlined
// Disabled:    <AppRadio checked={val} onChange={setVal} disabled />

export type RadioSize = 'xs' | 'sm' | 'md' | 'lg';
export type RadioColor =
  | 'primary'
  | 'blue'
  | 'purple'
  | 'green'
  | 'red'
  | 'orange'
  | 'teal';
export type RadioVariant = 'filled' | 'outlined';

export interface AppRadioProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  labelPosition?: 'left' | 'right';
  size?: RadioSize;
  color?: RadioColor;
  variant?: RadioVariant;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  value?: string;
  /** Render with white styling — use when placed on a filled/colored background */
  inverted?: boolean;
  testId?: string;
}

// Size tokens

const SIZE: Record<
  RadioSize,
  {
    outer: string;
    inner: string;
    text: 'sm' | 'md' | 'base' | 'lg';
    gap: string;
  }
> = {
  xs: {
    outer: 'w-3.5 h-3.5',
    inner: 'w-1.5 h-1.5',
    text: 'sm',
    gap: 'gap-1.5',
  },
  sm: { outer: 'w-4 h-4', inner: 'w-2 h-2', text: 'md', gap: 'gap-2' },
  md: {
    outer: 'w-[18px] h-[18px]',
    inner: 'w-2.5 h-2.5',
    text: 'base',
    gap: 'gap-2.5',
  },
  lg: { outer: 'w-5 h-5', inner: 'w-3 h-3', text: 'lg', gap: 'gap-3' },
};

// Color tokens - filled

const FILLED_BG: Record<RadioColor, string> = {
  primary: APP_ACCENT.primary.bg,
  blue: APP_ACCENT.blue.bg,
  purple: APP_ACCENT.purple.bg,
  green: APP_ACCENT.green.bg,
  red: APP_ACCENT.red.bg,
  orange: APP_ACCENT.orange.bg,
  teal: APP_ACCENT.teal.bg,
};

const FILLED_RING: Record<RadioColor, string> = {
  primary: APP_ACCENT.primary.ring,
  blue: APP_ACCENT.blue.ring,
  purple: APP_ACCENT.purple.ring,
  green: APP_ACCENT.green.ring,
  red: APP_ACCENT.red.ring,
  orange: APP_ACCENT.orange.ring,
  teal: APP_ACCENT.teal.ring,
};

const FILLED_DOT: Record<RadioColor, string> = {
  primary: APP_TEXT.white,
  blue: APP_TEXT.white,
  purple: APP_TEXT.white,
  green: APP_TEXT.white,
  red: APP_TEXT.white,
  orange: APP_TEXT.white,
  teal: APP_TEXT.white,
};

// Color tokens - outlined

const OUTLINED_BORDER: Record<RadioColor, string> = {
  primary: APP_ACCENT.primary.border,
  blue: APP_ACCENT.blue.border,
  purple: APP_ACCENT.purple.border,
  green: APP_ACCENT.green.border,
  red: APP_ACCENT.red.border,
  orange: APP_ACCENT.orange.border,
  teal: APP_ACCENT.teal.border,
};

const OUTLINED_DOT: Record<RadioColor, string> = {
  primary: APP_ACCENT.primary.bg,
  blue: APP_ACCENT.blue.bg,
  purple: APP_ACCENT.purple.bg,
  green: APP_ACCENT.green.bg,
  red: APP_ACCENT.red.bg,
  orange: APP_ACCENT.orange.bg,
  teal: APP_ACCENT.teal.bg,
};

const FOCUS_RING: Record<RadioColor, string> = {
  primary: 'focus-visible:ring-primary/30',
  blue: 'focus-visible:ring-blue-500/30',
  purple: 'focus-visible:ring-purple-500/30',
  green: 'focus-visible:ring-green-500/30',
  red: 'focus-visible:ring-red-500/30',
  orange: 'focus-visible:ring-orange-500/30',
  teal: 'focus-visible:ring-teal-500/30',
};

// Component

const AppRadio: FC<AppRadioProps> = ({
  checked,
  onChange,
  label,
  labelPosition = 'right',
  size = 'md',
  color = 'primary',
  variant = 'filled',
  disabled = false,
  className,
  id,
  inverted = false,
  testId
}) => {
  const s = SIZE[size];
  const filled = variant === 'filled';

  const dot = (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      id={id}
      disabled={disabled}
      onClick={() => !disabled && onChange(true)}
      className={cx(
        'relative inline-flex flex-shrink-0 items-center justify-center rounded-full transition-all duration-150 outline-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
        FOCUS_RING[color],
        disabled && 'opacity-40 cursor-not-allowed pointer-events-none',
      )}
      data-testid={testId || ""}
    >
      <span
        className={cx(
          'flex items-center justify-center rounded-full border-2 transition-all duration-150',
          s.outer,
          // inverted (on colored backgrounds)
          inverted && !checked && ['bg-white/10 border-white/50'],
          inverted && checked && ['border-white/80 bg-white/20 scale-[1.04]'],
          // filled - inactive
          !inverted &&
            filled &&
            !checked && [
              'bg-white dark:bg-gray-800',
              'border-gray-300 dark:border-gray-600',
              'hover:border-gray-400 dark:hover:border-gray-500',
            ],
          // filled - active
          !inverted &&
            filled &&
            checked && [
              'border-transparent scale-[1.04]',
              FILLED_BG[color],
              'ring-2',
              FILLED_RING[color],
            ],
          // outlined - inactive
          !inverted &&
            !filled &&
            !checked && [
              'bg-transparent',
              'border-gray-300 dark:border-gray-500',
              'hover:border-gray-400 dark:hover:border-gray-400',
            ],
          // outlined - active
          !inverted &&
            !filled &&
            checked && ['bg-transparent', OUTLINED_BORDER[color]],
        )}
      >
        {checked && (
          <span
            className={cx(
              'rounded-full',
              s.inner,
              inverted
                ? 'bg-white'
                : filled
                  ? FILLED_DOT[color]
                  : OUTLINED_DOT[color],
            )}
          />
        )}
      </span>
    </button>
  );

  if (!label) return dot;

  return (
    <label
      className={cx(
        'inline-flex max-w-full flex-wrap items-center cursor-pointer select-none',
        s.gap,
        disabled && 'opacity-40 cursor-not-allowed',
        className,
      )}
    >
      {labelPosition === 'left' && (
        <Text
          as="span"
          size={s.text}
          weight="medium"
          color="soft"
          leading="none"
          className="min-w-0 break-words"
        >
          {label}
        </Text>
      )}
      {dot}
      {labelPosition === 'right' && (
        <Text
          as="span"
          size={s.text}
          weight="medium"
          color="soft"
          leading="none"
          className="min-w-0 break-words"
        >
          {label}
        </Text>
      )}
    </label>
  );
};

export default AppRadio;
